import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { adminAPI, tradingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Users, Search, RefreshCw, TrendingUp, ArrowLeftRight, 
  Wallet, History, Home, LogOut, ChevronRight, User, X, Menu,
  DollarSign, BarChart3, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { logout, user } = useAuth();

  const navItems = [
    { path: '/admin/accounts', label: 'Account Switcher', icon: <ArrowLeftRight className="w-5 h-5" /> },
    { path: '/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/admin', label: 'Admin Panel', icon: <TrendingUp className="w-5 h-5" /> },
  ];

  return (
    <>
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setOpen(false)} 
        />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 
        bg-[#0f0f11] border-r border-zinc-800 
        transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        transition-transform duration-300 flex flex-col
      `}>
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
            <button onClick={() => setOpen(false)} className="lg:hidden text-zinc-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setOpen(false)}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <div className="text-xs text-zinc-500 mb-2">Logged in as Admin</div>
          <div className="text-sm text-white mb-3">{user?.email}</div>
          <button
            onClick={logout}
            className="nav-item w-full text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

function AccountCard({ account, onSwitch, isActive }) {
  const formatCurrency = (val) => {
    if (val === undefined || val === null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  };

  return (
    <div 
      className={`glass-card p-4 cursor-pointer transition-all hover:border-primary/50 ${
        isActive ? 'border-primary ring-1 ring-primary/30' : ''
      }`}
      onClick={() => onSwitch(account)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            account.is_admin ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
          }`}>
            <User className="w-5 h-5" />
          </div>
          <div>
            <div className="text-white font-medium">
              {account.first_name} {account.last_name}
            </div>
            <div className="text-zinc-500 text-sm">{account.email}</div>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-600" />
      </div>
      
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="bg-zinc-900/50 rounded-lg p-2">
          <div className="text-zinc-500 text-xs">Account ID</div>
          <div className="text-white font-mono text-sm">{account.account_id || 'N/A'}</div>
        </div>
        <div className="bg-zinc-900/50 rounded-lg p-2">
          <div className="text-zinc-500 text-xs">Balance</div>
          <div className="text-green-400 font-mono text-sm">{formatCurrency(account.balance)}</div>
        </div>
      </div>
      
      <div className="mt-3 flex items-center justify-between">
        <span className={`text-xs px-2 py-0.5 rounded ${
          account.kyc_status === 'verified' ? 'bg-green-500/20 text-green-400' :
          account.kyc_status === 'submitted' ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-zinc-500/20 text-zinc-400'
        }`}>
          KYC: {account.kyc_status || 'Pending'}
        </span>
        <span className="text-xs text-zinc-500">
          {account.account_type || 'Standard'}
        </span>
      </div>
    </div>
  );
}

function AccountDetails({ account, trades, onClose, onManageTrades }) {
  const formatCurrency = (val) => {
    if (val === undefined || val === null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  };

  const openPnL = trades?.open_trades?.reduce((sum, t) => sum + (t.pnl || 0), 0) || 0;

  return (
    <div className="glass-card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">Account Details</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Account Info */}
        <div className="bg-zinc-900/50 rounded-lg p-3">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
              <User className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <div className="text-white font-medium">{account.first_name} {account.last_name}</div>
              <div className="text-zinc-500 text-sm">{account.email}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-zinc-500">Account ID:</span> <span className="text-white font-mono">{account.account_id}</span></div>
            <div><span className="text-zinc-500">Type:</span> <span className="text-white">{account.account_type}</span></div>
          </div>
        </div>

        {/* Balance Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-zinc-900/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
              <Wallet className="w-3 h-3" /> Balance
            </div>
            <div className="text-green-400 font-mono text-lg">{formatCurrency(account.balance)}</div>
          </div>
          <div className="bg-zinc-900/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
              <BarChart3 className="w-3 h-3" /> Open P/L
            </div>
            <div className={`font-mono text-lg ${openPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(openPnL)}
            </div>
          </div>
        </div>

        {/* Trade Stats */}
        <div className="bg-zinc-900/50 rounded-lg p-3">
          <div className="text-zinc-400 text-xs font-medium mb-2">TRADING ACTIVITY</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Open Positions:</span>
              <span className="text-white font-mono">{trades?.total_open || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Closed Trades:</span>
              <span className="text-white font-mono">{trades?.total_closed || 0}</span>
            </div>
          </div>
        </div>

        {/* Open Positions */}
        {trades?.open_trades?.length > 0 && (
          <div>
            <div className="text-zinc-400 text-xs font-medium mb-2">OPEN POSITIONS</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {trades.open_trades.map((trade) => (
                <div key={trade.id} className="bg-zinc-900/50 rounded p-2 flex items-center justify-between">
                  <div>
                    <span className="text-white text-sm">{trade.pair}</span>
                    <span className={`ml-2 text-xs ${trade.direction === 'buy' ? 'text-blue-400' : 'text-red-400'}`}>
                      {trade.direction.toUpperCase()} {trade.volume}
                    </span>
                  </div>
                  <span className={`font-mono text-sm ${(trade.pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(trade.pnl || 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            className="flex-1 bg-primary hover:bg-primary/90"
            onClick={onManageTrades}
          >
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Manage Trades
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminAccountsPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedTrades, setSelectedTrades] = useState(null);
  const [switching, setSwitching] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getAllUsers();
      setAccounts(res.data);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchAccounts();
  }, [user, navigate, fetchAccounts]);

  const handleSelectAccount = async (account) => {
    setSelectedAccount(account);
    try {
      const res = await adminAPI.getUserTrades(account.id);
      setSelectedTrades(res.data);
    } catch (error) {
      console.error('Failed to fetch trades:', error);
    }
  };

  const handleSwitchAccount = async () => {
    if (!selectedAccount) return;
    
    setSwitching(true);
    try {
      const res = await adminAPI.switchAccount(selectedAccount.id);
      
      // Store the token and update auth state
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('adminOriginalToken', localStorage.getItem('token'));
      
      toast.success(`Switched to ${selectedAccount.email}`);
      
      // Reload to update auth state
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to switch account');
    } finally {
      setSwitching(false);
    }
  };

  const filteredAccounts = accounts.filter((acc) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      acc.email?.toLowerCase().includes(query) ||
      acc.first_name?.toLowerCase().includes(query) ||
      acc.last_name?.toLowerCase().includes(query) ||
      acc.account_id?.toLowerCase().includes(query)
    );
  });

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <main className="flex-1 min-w-0">
        {/* Header */}
        <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-white font-medium flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Account Switcher
              </h1>
              <span className="text-zinc-500 text-sm">{accounts.length} accounts</span>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAccounts}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </header>

        <div className="p-4">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Search by name, email, or account ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900/50 border-zinc-800 text-white"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Account List */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No accounts found</p>
                </div>
              ) : (
                <ScrollArea className="h-[calc(100vh-180px)]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-4">
                    {filteredAccounts.map((account) => (
                      <AccountCard
                        key={account.id}
                        account={account}
                        onSwitch={handleSelectAccount}
                        isActive={selectedAccount?.id === account.id}
                      />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Selected Account Details */}
            <div className="lg:col-span-1">
              {selectedAccount ? (
                <AccountDetails
                  account={selectedAccount}
                  trades={selectedTrades}
                  onClose={() => {
                    setSelectedAccount(null);
                    setSelectedTrades(null);
                  }}
                  onManageTrades={handleSwitchAccount}
                />
              ) : (
                <div className="glass-card p-6 text-center">
                  <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                  <p className="text-zinc-400">Select an account to view details and manage trades</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
